import { useNavigate } from '@tanstack/react-router';
import { type ReactNode } from 'react';
import { z } from 'zod';

import { type HarnessScope } from '@apps/web/features/phoebe_chat_v3/model/event_types';
import { AgencyRulesPreviewCard } from '@apps/web/features/phoebe_chat_v3/ui/primitives/agency_rules_preview_card';
import { useAgentRunWorkspaceFileRows } from '@apps/web/hooks/use_agent_run_workspace_file_rows';

const PREVIEW_RULE_COUNT = 2;
const agencyRuleRowsSchema = z.array(
    z
        .object({
            rule: z.string().trim().min(1).max(240),
        })
        .strict(),
);

export type AgencyRulesPreviewResourceProps = Readonly<{
    readonly scope: HarnessScope;
    readonly workspacePath: string;
    readonly rowCount: number;
    readonly sourceEventId: string;
}>;

export function AgencyRulesPreviewResource({
    scope,
    workspacePath,
    rowCount,
    sourceEventId,
}: AgencyRulesPreviewResourceProps): ReactNode {
    const navigate = useNavigate();
    const workspaceRows = useAgentRunWorkspaceFileRows({
        organizationId: scope.organizationId,
        runId: scope.runId,
        filePath: workspacePath,
        format: 'jsonl',
        eventId: sourceEventId,
        mode: scope.mode,
    });
    const parsedRows = agencyRuleRowsSchema.safeParse(workspaceRows.rows);

    if (workspaceRows.isLoading) {
        return (
            <span role="status" className="text-muted-foreground text-v3-chat">
                Loading agency rules…
            </span>
        );
    }
    if (
        workspaceRows.error !== undefined ||
        !parsedRows.success ||
        parsedRows.data.length === 0
    ) {
        return (
            <span role="alert" className="text-muted-foreground text-v3-chat">
                Couldn’t load agency rules.
            </span>
        );
    }

    return (
        <AgencyRulesPreviewCard
            agencyName={scope.organizationName ?? 'Your agency'}
            rules={parsedRows.data
                .slice(0, PREVIEW_RULE_COUNT)
                .map(({ rule }) => rule)}
            totalRuleCount={rowCount}
            onViewAll={() => void navigate({ to: '/playbooks' })}
        />
    );
}
